import { StateCreator } from 'zustand';
import type { SortingState } from '@tanstack/react-table';

import type {
  BillingReadinessCategory,
  PayorType,
  RemittanceSource,
  RemittanceStatus,
} from '../../services/api/types.gen';

export type BillingTabKey =
  | 'not-ready'
  | 'ready'
  | 'pending-approval'
  | 'ready-to-send'
  | 'pending-payment'
  | 'paid'
  | 'rejected-cancelled'
  | 'denied';

/** Persisted toolbar state for one billing tab. pageIndex is intentionally NOT here. */
export interface BillingTabUiState {
  /** Server-side free-text search (debounced in the toolbar before write). */
  globalFilter: string;
  /** Selected insurance ids, sent as insurance_ids to the backend. */
  insuranceIds: string[];
  /** Selected clinic ids, sent as clinic_ids to the backend. */
  clinicIds: string[];
  /**
   * Selected agency-insurance payor types, sent as `payor_types`. Convenience
   * filter that intersects with `insuranceIds` on the backend. Optional so a
   * persisted entry written before this field existed reads as absent; the
   * query hook falls back to a stable empty array.
   */
  payorTypes?: PayorType[];
  /** Single-column sort; sorting[0] maps to sort_by/sort_order (server is single-sort). */
  sorting: SortingState;
  /** Server page size (per_page). pageIndex is NOT persisted. */
  pageSize: number;
  /**
   * Not Ready failure-category filter, sent as `failing_categories`.
   * Optional so a persisted entry written before this field existed reads as
   * absent; the query hook falls back to a stable empty array. Only the Not
   * Ready tab sends it.
   */
  failingCategories?: BillingReadinessCategory[];
}

export const DEFAULT_BILLING_TAB_UI: BillingTabUiState = {
  globalFilter: '',
  insuranceIds: [],
  clinicIds: [],
  payorTypes: [],
  sorting: [],
  pageSize: 25,
  failingCategories: [],
};

export const BILLING_TAB_DEFAULT_SORTING: Record<BillingTabKey, SortingState> =
  {
    'not-ready': [{ id: 'claim_age', desc: true }],
    ready: [{ id: 'claim_age', desc: true }],
    'pending-approval': [{ id: 'claim_age', desc: true }],
    'ready-to-send': [{ id: 'claim_age', desc: true }],
    'pending-payment': [{ id: 'claim_age', desc: true }],
    // Rejected cycles need a biller's action; cancelled ones do not. desc
    // sorts the status names so REJECTED lands above CANCELLED.
    // R-billing-status.req-23 Rejected cycles first
    'rejected-cancelled': [{ id: 'status', desc: true }],
    denied: [{ id: 'claim_age', desc: true }],
    paid: [{ id: 'claim_age', desc: true }],
  };

/** Per-tab default state, memoized so `getBillingTabUi` returns a STABLE reference
 *  for unpersisted tabs. Zustand compares snapshots by identity; returning a fresh
 *  object each call triggers a "getSnapshot should be cached" infinite render loop. */
const DEFAULT_BILLING_TAB_UI_BY_TAB: Record<BillingTabKey, BillingTabUiState> =
  Object.fromEntries(
    (Object.keys(BILLING_TAB_DEFAULT_SORTING) as BillingTabKey[]).map(tab => [
      tab,
      { ...DEFAULT_BILLING_TAB_UI, sorting: BILLING_TAB_DEFAULT_SORTING[tab] },
    ])
  ) as Record<BillingTabKey, BillingTabUiState>;

export function defaultBillingTabUi(tab: BillingTabKey): BillingTabUiState {
  return DEFAULT_BILLING_TAB_UI_BY_TAB[tab];
}

/** Column ids the table currently renders as sortable (`enableSorting`) and that the
 *  backend can order by. A persisted sort referencing any other id is stale — e.g. the
 *  removed client-side `days_to_pay` / `total_anticipated_payment` / `tob` columns, or
 *  the now sort-disabled `total_charges`. The backend silently falls back to default
 *  ordering for an unknown `sort_by`, so such sorts must be dropped to the tab default
 *  during migration rather than sent verbatim. */
const SUPPORTED_BILLING_SORT_IDS: ReadonlySet<string> = new Set([
  'patient_name',
  'clinic_name',
  'insurance_name',
  'billing_period',
  'claim_age',
  'claim_number',
  'branch',
  'status',
]);

/** Resolve a persisted sort to a single supported entry, else the tab default.
 *  The server is single-sort, so only the first sort entry is meaningful. */
function sanitizeBillingSorting(
  sorting: SortingState | undefined,
  tab: BillingTabKey
): SortingState {
  const first = sorting?.[0];
  if (first && SUPPORTED_BILLING_SORT_IDS.has(first.id)) {
    return [first];
  }
  return BILLING_TAB_DEFAULT_SORTING[tab];
}

/** One-time migration of persisted state:
 *  - sanitize persisted sorting: keep a supported single-column sort, otherwise fall
 *    back to the tab default urgency sort (covers stale EMPTY sorts and sorts on
 *    columns no longer server-sortable);
 *  - drop legacy client-side `columnFilters` (server-side filters replaced them with
 *    `insuranceIds`/`clinicIds`) and ensure the new filter arrays exist for any
 *    pre-migration persisted entry. */
export function migrateBillingUiState(
  state: Partial<Record<BillingTabKey, BillingTabUiState>>
): Partial<Record<BillingTabKey, BillingTabUiState>> {
  const next: Partial<Record<BillingTabKey, BillingTabUiState>> = { ...state };
  (Object.keys(next) as BillingTabKey[]).forEach(tab => {
    const entry = next[tab] as
      (Partial<BillingTabUiState> & { columnFilters?: unknown }) | undefined;
    if (!entry) return;
    const { columnFilters: _legacyColumnFilters, ...rest } = entry;
    void _legacyColumnFilters;
    next[tab] = {
      globalFilter: rest.globalFilter ?? '',
      sorting: sanitizeBillingSorting(rest.sorting, tab),
      pageSize: rest.pageSize ?? 25,
      insuranceIds: rest.insuranceIds ?? [],
      clinicIds: rest.clinicIds ?? [],
      payorTypes: rest.payorTypes ?? [],
    };
  });
  return next;
}

/** Store v7: Rejected / Cancelled used to default to claim_age desc. A persisted
 *  copy of that old default cannot be told from a biller's own pick, so it moves
 *  to the new status-first default. Every other tab, and any other sort on this
 *  tab, is left exactly as persisted. */
export function migrateRejectedCancelledDefaultSort(
  state: Partial<Record<BillingTabKey, BillingTabUiState>>
): Partial<Record<BillingTabKey, BillingTabUiState>> {
  const entry = state['rejected-cancelled'];
  const first = entry?.sorting?.[0];
  if (!entry || first?.id !== 'claim_age' || !first.desc) return state;
  return {
    ...state,
    'rejected-cancelled': {
      ...entry,
      sorting: BILLING_TAB_DEFAULT_SORTING['rejected-cancelled'],
    },
  };
}

/** Persisted toolbar state for the Remittances view. pageIndex is intentionally NOT here.
 *  Deposit dates are ISO 'YYYY-MM-DD' STRINGS, never Date objects: this slice is
 *  JSON-persisted, so a Date rehydrates from localStorage as a string while still
 *  typed Date, which crashes DatePicker and any .getTime()/.toISOString() call. */
export interface RemittanceUiState {
  /** Server-side free-text search (debounced in the toolbar before write). */
  globalFilter: string;
  /** The selected status tab, sent as the single `status` param. This is
   *  navigation, not a filter, so `resetRemittanceUi` preserves it — clearing a
   *  claims tab's toolbar likewise leaves you on that tab. */
  statusTab: RemittanceStatus;
  /** RemittanceSource values, sent as repeated `source` params. */
  sources: RemittanceSource[];
  /** Payor ids, sent as repeated `payor_id` params. */
  payorIds: string[];
  depositDateFrom: string | null;
  depositDateTo: string | null;
  /** Single-column sort; sorting[0] maps to sort_by/sort_order (server is single-sort). */
  sorting: SortingState;
  pageSize: number;
}

/** Default sort is a VISIBLE, sortable column so the header arrow is truthful on
 *  first load (the backend's own no-sort fallback is received_at, which the table
 *  does not render). */
export const REMITTANCE_DEFAULT_SORTING: SortingState = [
  { id: 'deposit_date', desc: true },
];

/** The status tab shown on first visit — the head of the remittance pipeline. */
export const DEFAULT_REMITTANCE_STATUS_TAB: RemittanceStatus = 'saved';

/** Valid status tab ids, used to sanitize a persisted value on read. */
export const REMITTANCE_STATUS_TAB_IDS: ReadonlySet<string> = new Set<string>([
  'saved',
  'completed',
]);

/** Column ids the remittance table renders sortable AND the backend can order by. */
export const SUPPORTED_REMITTANCE_SORT_IDS: ReadonlySet<string> = new Set([
  'deposit_date',
  'check_date',
  'trace_number',
  'payer_display',
  'status',
  'source',
  'amount',
  'claim_count',
]);

/** Module-level constant = a STABLE reference. Zustand compares snapshots by
 *  identity; a store read that builds a fresh object per call triggers React's
 *  "getSnapshot should be cached" infinite render loop (the same trap the
 *  DEFAULT_BILLING_TAB_UI_BY_TAB comment above documents). remittanceUiState is
 *  therefore a plain non-optional field, NOT a getter. */
export const DEFAULT_REMITTANCE_UI: RemittanceUiState = {
  globalFilter: '',
  statusTab: DEFAULT_REMITTANCE_STATUS_TAB,
  sources: [],
  payorIds: [],
  depositDateFrom: null,
  depositDateTo: null,
  sorting: REMITTANCE_DEFAULT_SORTING,
  pageSize: 25,
};

export interface BillingUiSlice {
  /**
   * Page-wide, not per-tab: a setting that changed when you switched tabs
   * would be a bug. Persisted through the store's `persist` middleware, which
   * lists `hideTestPatients` in `partialize` beside `billingUiState`.
   * Defaults to hidden; a persisted explicit choice wins because Zustand's
   * shallow persistence merge only overwrites keys the payload carries.
   */
  hideTestPatients: boolean;
  setHideTestPatients: (hide: boolean) => void;
  /** Imported patient charts stay out of Claims until explicitly included. */
  hideImportedPatients: boolean;
  setHideImportedPatients: (hide: boolean) => void;
  /** Exclude cycles ending before the confirmed take-over day on their admission (end_date). */
  excludePreTakeover: boolean;
  setExcludePreTakeover: (exclude: boolean) => void;
  showInactiveEpisodes: boolean;
  setShowInactiveEpisodes: (show: boolean) => void;
  /**
   * Page-wide and persisted the same way as `showInactiveEpisodes`.
   * Draft cycles with nothing billable stay out of Not Ready until a user
   * opts in; a payload persisted before this key existed reads as the `false`
   * default through the same shallow merge.
   */
  showNothingToBill: boolean;
  setShowNothingToBill: (show: boolean) => void;
  billingUiState: Partial<Record<BillingTabKey, BillingTabUiState>>;
  getBillingTabUi: (tab: BillingTabKey) => BillingTabUiState;
  setBillingTabUi: (
    tab: BillingTabKey,
    patch: Partial<BillingTabUiState>
  ) => void;
  resetBillingTabUi: (tab: BillingTabKey) => void;
  remittanceUiState: RemittanceUiState;
  setRemittanceUi: (patch: Partial<RemittanceUiState>) => void;
  resetRemittanceUi: () => void;
}

export const billingUiSlice: StateCreator<BillingUiSlice> = (set, get) => ({
  // Test patients stay hidden until a user explicitly reveals them; the
  // checkbox choice persists across tabs and browser sessions.
  hideTestPatients: true,
  setHideTestPatients: hide => set({ hideTestPatients: hide }),
  hideImportedPatients: true,
  setHideImportedPatients: hide => set({ hideImportedPatients: hide }),
  excludePreTakeover: true,
  setExcludePreTakeover: exclude => set({ excludePreTakeover: exclude }),
  showInactiveEpisodes: false,
  setShowInactiveEpisodes: show => set({ showInactiveEpisodes: show }),
  showNothingToBill: false,
  setShowNothingToBill: show => set({ showNothingToBill: show }),
  billingUiState: {},
  getBillingTabUi: tab => get().billingUiState[tab] ?? defaultBillingTabUi(tab),
  setBillingTabUi: (tab, patch) =>
    set(state => ({
      billingUiState: {
        ...state.billingUiState,
        [tab]: {
          ...(state.billingUiState[tab] ?? defaultBillingTabUi(tab)),
          ...patch,
        },
      },
    })),
  resetBillingTabUi: tab =>
    set(state => ({
      billingUiState: {
        ...state.billingUiState,
        [tab]: defaultBillingTabUi(tab),
      },
    })),
  remittanceUiState: DEFAULT_REMITTANCE_UI,
  setRemittanceUi: patch =>
    set(state => ({
      remittanceUiState: { ...state.remittanceUiState, ...patch },
    })),
  // Reset clears the toolbar filters but keeps the caller on their current
  // status tab (see RemittanceUiState.statusTab).
  resetRemittanceUi: () =>
    set(state => ({
      remittanceUiState: {
        ...DEFAULT_REMITTANCE_UI,
        statusTab: state.remittanceUiState.statusTab,
      },
    })),
});
